/**
 * Metric — show view on the shared Inspector grammar (one-grammar law).
 * Overview leads with the meaningful fields (name carried in the Header, unit,
 * owner); the description renders as markdown in a Body section; the
 * domain-specific recent-measurements time-series table is preserved INSIDE its
 * own Inspector section (with a "Record measurement" aside action). Owner +
 * timestamps sink to a muted Technical tail; Audit closes the page.
 */
import {
  useShow,
  useList,
  useGo,
  useParsed,
  type HttpError,
} from "@refinedev/core";
import { Pencil } from "@/components/ui/icon";

import type { Metric, Measurement } from "@aos/shared";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
} from "@/components/inspector";
import { EmptyState, LoadingState } from "@/components/ui/states";
import { Markdown } from "@/components/app";
import { formatDateTime } from "@/lib/format";

/** MetricShow — metric detail with its recent measurements time-series. */
export function MetricShow() {
  const go = useGo();
  const { id } = useParsed();
  const { query } = useShow<Metric, HttpError>({ resource: "metrics" });
  const { data, isLoading, isError, error, refetch } = query;
  const metric = data?.data;

  const { data: mData, isLoading: mLoading } = useList<Measurement>({
    resource: "measurements",
    filters: [{ field: "metric_id", operator: "eq", value: id }],
    sorters: [{ field: "measured_at", order: "desc" }],
    pagination: { pageSize: 50 },
    queryOptions: { enabled: id != null },
  });
  const measurements = mData?.data ?? [];

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !metric) {
    return (
      <ShowPageError
        message={error?.message ?? "Metric not found."}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <Inspector>
      <InspectorHeader
        title={metric.name}
        subtitle="A measurable metric — its unit, owner, and recorded measurements."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              go({ to: { resource: "metrics", action: "edit", id: metric.id } })
            }
          >
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </Button>
        }
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Unit">
            {metric.unit ? (
              <span className="font-mono text-sm">{metric.unit}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </Field>
          <Field label="Owner">
            <RefLink resource="users" id={metric.owner_user_id} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      {metric.description && metric.description.trim() ? (
        <InspectorSection title="Description">
          <Markdown source={metric.description} />
        </InspectorSection>
      ) : null}

      <InspectorSection
        title="Measurements"
        aside={
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              go({ to: { resource: "measurements", action: "create" } })
            }
          >
            Record measurement
          </Button>
        }
      >
        {mLoading ? (
          <LoadingState label="Loading measurements…" rows={3} />
        ) : measurements.length === 0 ? (
          <EmptyState
            icon="okrs"
            message="No measurements recorded yet."
            hint="Record one to start tracking this metric over time."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Measured at</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead>Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {measurements.map((m) => (
                <TableRow
                  key={m.id}
                  className="cursor-pointer"
                  onClick={() =>
                    go({
                      to: { resource: "measurements", action: "show", id: m.id },
                    })
                  }
                >
                  <TableCell>{formatDateTime(m.measured_at)}</TableCell>
                  <TableCell className="text-right font-medium">
                    {m.value}
                    {metric.unit ? ` ${metric.unit}` : ""}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {m.source_system ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={metric.id} />
          </Field>
          <Field label="Created">{formatDateTime(metric.created_at)}</Field>
          <Field label="Updated">{formatDateTime(metric.updated_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="metrics" entityId={metric.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default MetricShow;
